import { Stethoscope, Users, Building2, Hospital, HeartPulse, Check } from "lucide-react";
import { CityData } from "@/utils/cityData";
import ResponsiveSection from "@/components/layout/ResponsiveSection";
import { ScrollAnimatedSection } from "@/components/shared/ScrollAnimatedSection";
import { InteractiveCard } from "@/components/ui/interactive-card";
import { Card, CardContent } from "@/components/ui/card";

interface PracticeTypesShowcaseProps {
  city: CityData;
}

const PracticeTypesShowcase = ({ city }: PracticeTypesShowcaseProps) => {
  const practiceTypes = [
    {
      icon: Stethoscope,
      title: "Hausarztpraxen",
      points: [
        "Patientendaten-Schutz",
        "Praxisverwaltungssoftware-Absicherung",
        "Schnelle Wiederherstellung"
      ],
      coverage: "ab 250.000 €"
    },
    {
      icon: HeartPulse,
      title: "Zahnarztpraxen",
      points: [
        "DSGVO-Compliance",
        "Röntgendaten-Sicherung",
        "Terminverwaltungs-Schutz"
      ],
      coverage: "ab 250.000 €"
    },
    {
      icon: Users,
      title: "Facharztpraxen",
      points: [
        "Spezialdaten-Schutz",
        "Labor-Anbindung abgesichert",
        "Notfall-Support 24/7"
      ],
      coverage: "ab 500.000 €"
    },
    {
      icon: Building2,
      title: "MVZ & Gemeinschaftspraxen",
      points: [
        "Multi-Standort-Schutz",
        "Zentrale Datenverwaltung",
        "Erweiterte Deckung"
      ],
      coverage: "ab 1.000.000 €"
    },
    {
      icon: Hospital,
      title: "Kliniken & Gesundheitszentren",
      points: [
        "Krankenhaus-IT-Schutz",
        "Kritische Infrastruktur",
        "Premium-Absicherung"
      ],
      coverage: "ab 2.000.000 €"
    }
  ];

  return (
    <ResponsiveSection spacing="lg">
      <ScrollAnimatedSection>
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center break-words">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-primary">
            Für wen ist die Cyberversicherung in {city.name} geeignet?
          </span>
        </h2>
        <p className="text-sm text-muted-foreground mb-12 text-center">
          Maßgeschneiderter Schutz für jede Praxisform in {city.name}
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          {practiceTypes.map((type, index) => {
            const Icon = type.icon;
            return (
              <InteractiveCard 
                key={index}
                enableTilt={true} 
                enablePulse={true} 
                enableDynamicShadow={true}
                className="border-border/50 hover:border-accent/50"
              >
                <CardContent className="pt-6">
                  <Icon className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
                  <h3 className="font-bold text-foreground mb-3 text-center text-sm sm:text-base">{type.title}</h3>
                  <ul className="space-y-2 text-xs sm:text-sm text-foreground/70 mb-4">
                    {type.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="pt-4 border-t border-accent/20">
                    <p className="text-xs text-muted-foreground text-center">Empfohlene Deckung</p>
                    <p className="text-lg font-bold text-accent text-center">{type.coverage}</p>
                  </div>
                </CardContent>
              </InteractiveCard>
            );
          })}
        </div>
      </ScrollAnimatedSection>
    </ResponsiveSection>
  );
};

export default PracticeTypesShowcase;
